import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Back-control state: menu dismissers for hardware back, and a count of mounted
// back controls (the automatic back bar shows only when there are none).
const BackNavigationContext = createContext(null);

export const BackNavigationProvider = ({ children }) => {
  const [dismissers, setDismissers] = useState([]);
  const [ownerCount, setOwnerCount] = useState(0);

  const registerDismisser = useCallback((entry) => {
    setDismissers((list) => [...list, entry]);
    return () => setDismissers((list) => list.filter((e) => e !== entry));
  }, []);

  const registerOwner = useCallback(() => {
    setOwnerCount((n) => n + 1);
    return () => setOwnerCount((n) => n - 1);
  }, []);

  const value = useMemo(
    () => ({ dismissers, ownerCount, registerDismisser, registerOwner }),
    [dismissers, ownerCount, registerDismisser, registerOwner],
  );

  return (
    <BackNavigationContext.Provider value={value}>
      {children}
    </BackNavigationContext.Provider>
  );
};

export const useBackNavigationContext = () => useContext(BackNavigationContext);

/** While `active`, Android's hardware back runs the latest `dismiss` (e.g. close a menu) instead of leaving the page. */
export const useBackDismiss = (dismiss, active) => {
  const register = useContext(BackNavigationContext)?.registerDismisser;
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  useEffect(() => {
    if (!active || !register) {
      return undefined;
    }
    return register({ run: () => dismissRef.current?.() });
  }, [active, register]);
};

/** Count the caller as a mounted back control while `enabled`. */
export const useRegisterBackOwner = (enabled = true) => {
  const register = useContext(BackNavigationContext)?.registerOwner;

  // Layout effect so the automatic bar never paints alongside the owner.
  useLayoutEffect(() => {
    if (!enabled || !register) {
      return undefined;
    }
    return register();
  }, [enabled, register]);
};

export default BackNavigationContext;
